/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Loads the Account 360 rollups (backend Routes/account_summary.py): the core
 * summary always, the travel and real-estate summaries only when the tenant
 * can have them. Each is independent; one that fails (not bought, not
 * deployed yet, no permission) comes back null and its tabs and tiles hide.
 */

import { useCallback, useEffect, useState } from "react";
import { useAuth } from "auth/Auth";

export default function useAccount360Summary(accountId, { travel = false, realEstate = false } = {}) {
  const { axiosInstance } = useAuth();
  const [state, setState] = useState({ summary: null, travel: null, realEstate: null, loading: true });

  const load = useCallback(async () => {
    if (!axiosInstance || !accountId) return;
    const get = (path) =>
      axiosInstance
        .get(path)
        .then((r) => r.data)
        .catch(() => null);
    const [summary, travelData, reData] = await Promise.all([
      get(`/accounts/${accountId}/summary`),
      travel ? get(`/accounts/${accountId}/travel-summary`) : Promise.resolve(null),
      realEstate ? get(`/accounts/${accountId}/real-estate-summary`) : Promise.resolve(null),
    ]);
    setState({ summary, travel: travelData, realEstate: reData, loading: false });
  }, [axiosInstance, accountId, travel, realEstate]);

  useEffect(() => {
    load();
  }, [load]);

  return { ...state, reload: load };
}
